import './polyfills/array-at';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit'
import thunk from 'redux-thunk';
import { sessionService } from 'redux-react-session';
// Material-UI
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider  } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import './index.css';
// Reducers
import reducer from './reducers';
// Components
import ErrorSnackbar from './components/Common/ErrorSnackbar/ErrorSnackbar';
import SuccessSnackbar from 'components/Common/SuccessSnackbar/SuccessSnackbar';
import App from 'components/App/App';
// Sentry
import { withErrorBoundary, captureException } from '@sentry/react';
import './services/sentry';
// Helpers
import { isIndexedDBError } from 'helpers';

// Handle unhandled promise rejections, especially for Stripe.js loading failures
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  
  // Handle Stripe.js loading failures gracefully
  if (error && (
    (error.message && error.message.includes('Failed to load Stripe.js')) ||
    (error.message && error.message.includes('Stripe.js not available'))
  )) {
    // Log to Sentry but prevent default error handling
    captureException(error, {
      tags: {
        errorType: 'UnhandledStripeRejection',
        handled: true
      },
      extra: {
        message: 'Caught unhandled Stripe.js loading rejection',
        preventDefault: true
      }
    });
    
    // Prevent the error from being logged to console as unhandled
    event.preventDefault();
  }
  
  // Handle IndexedDB errors gracefully - these are browser-level issues, particularly on iOS Safari
  if (error && isIndexedDBError(error)) {
    // These are browser bugs, not application errors - prevent them from being logged as unhandled
    // The sessionService will handle fallback behavior
    event.preventDefault();
  }
  
  // Handle "Session not found" errors gracefully - these are expected when session is already deleted
  if (error && (
    (typeof error === 'string' && error.toLowerCase().includes('session not found')) ||
    (error?.message && error.message.toLowerCase().includes('session not found'))
  )) {
    // This is an expected error when trying to delete a session that doesn't exist
    // Prevent it from being logged as an unhandled rejection
    event.preventDefault();
  }
});

const store = configureStore({ reducer, middleware: [thunk] });
const container = document.getElementById('root');
const root = createRoot(container);

// Initialize session service with error handling for IndexedDB issues
try {
  sessionService.initSessionService(store);
} catch (error) {
  // If IndexedDB initialization fails, silently handle it - don't log to avoid Sentry noise
  // The sessionService should handle fallback to localStorage internally
  if (error && isIndexedDBError(error)) {
    // Silently handle IndexedDB errors - these are browser-level issues, not application bugs
    // No need to log as Sentry's console logging integration would capture it
  } else {
    // Re-throw non-IndexedDB errors
    throw error;
  }
}

// Wrap the app with Sentry's error boundary
const SentryApp = withErrorBoundary(App, {
  fallback: <div>An error has occurred</div>,
  showDialog: true,
  onError: (error, componentStack, eventId) => {
    // Don't capture IndexedDB errors - these are browser-level issues, particularly on iOS Safari
    if (error && isIndexedDBError(error)) {
      return; // Silently skip IndexedDB errors
    }
    // Log additional context when an error boundary catches an error
    captureException(error, {
      extra: {
        componentStack,
        eventId
      }
    });
  }
});

root.render(
  <Provider store={store}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorSnackbar />
        <SuccessSnackbar />
        <SentryApp />
      </ThemeProvider>
    </StyledEngineProvider>
  </Provider>
);

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.ready.then(registration => {
    registration.unregister();

    if (caches) {
      // Service worker cache should be cleared with caches.delete()
      caches.keys().then(async (names) => {
        await Promise.all(names.map(name => caches.delete(name)));
      });
    }
  });
}